import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';
import _ from 'lodash';
import { useFormik } from 'formik';
import { useMutation, useQuery } from 'react-fetching-library';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Modal,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';

// import defaultImage from './default.png';
import { getNotificationSettingsDetail } from '../../../../services/request';
import { useAccounts } from '../Context';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};

const validationSchema = Yup.object({
  group: Yup.string(),
  send_email_with_credentials: Yup.boolean(),
  employee: Yup.string().required('To pole nie może być puste'),
  camps: Yup.array(),
  zs_weeks_periods: Yup.array()
});

const getCreateAccountErrorMessage = payload => {
  if (_.isArray(payload) && payload[0]) {
    return payload[0];
  }

  if (_.isString(payload) && payload.trim()) {
    return payload;
  }

  if (_.isPlainObject(payload)) {
    return _.chain(payload)
      .values()
      .flatMap(value => (_.isArray(value) ? value : [value]))
      .map(value => (_.isPlainObject(value) ? JSON.stringify(value) : String(value)))
      .filter(Boolean)
      .join(' ')
      .value();
  }

  return 'Nie udało się utworzyć konta';
};

export default function CreateAccountForm({ rows, createAccount, setOpen }) {
  const { mutate, error, loading, reset } = useMutation(createAccount);
  const { setRows, choices } = useAccounts();
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [createErrorMessage, setCreateErrorMessage] = useState('');
  const employeeSelectContainerRef = useRef(null);
  const [employeeMenuWidth, setEmployeeMenuWidth] = useState(520);

  useEffect(() => {
    const updateWidth = () => {
      const measuredWidth = employeeSelectContainerRef.current?.clientWidth;
      setEmployeeMenuWidth(measuredWidth || 520);
    };

    updateWidth();

    if (
      typeof ResizeObserver !== 'undefined' &&
      employeeSelectContainerRef.current
    ) {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(employeeSelectContainerRef.current);

      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateWidth);

    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const takenEmployeeIds = useMemo(
    () => new Set(_.chain(rows).map(row => row?.employee?.id)
      .filter(Boolean)
      .value()),
    [rows]
  );

  const employeeOptions = useMemo(
    () => _.map(choices.employees || [], employee => {
      const applications = _.map(employee.applications || [], app => {
        if (_.isObject(app)) {
          return {
            id: app.id,
            label: app.label || app.name || ''
          };
        }

        return {
          id: app,
          label: ''
        };
      });

      const applicationsLabel = !_.isEmpty(applications)
        ? _.map(
          applications,
          app => `#${app.id}${app.label ? ` ${app.label}` : ''}`
        ).join(', ')
        : 'Brak aplikacji';
      const email = employee.email || '';

      return {
        ...employee,
        email,
        applications,
        applicationsLabel,
        disabled: takenEmployeeIds.has(employee.value),
        searchIndex:
        `${employee.label} ${email} ${applicationsLabel}`.toLowerCase()
      };
    }),
    [choices.employees, takenEmployeeIds]
  );

  const filteredEmployeeOptions = useMemo(() => {
    const normalizedSearch = employeeSearch.trim().toLowerCase();
    if (!normalizedSearch) {
      return employeeOptions;
    }

    return _.filter(employeeOptions, option => option.searchIndex.includes(normalizedSearch));
  }, [employeeOptions, employeeSearch]);

  const handleClose = () => {
    reset();
    setCreateErrorMessage('');
    setOpen(false);
  };

  const handleSubmit = async values => {
    const requestBody = {
      ...values,
      camps: values.group === 'managers' ? values.camps : [],
      zs_weeks_periods:
        values.group === 'managers' ? values.zs_weeks_periods : []
    };
    const { error: mutateError, payload } = await mutate(null, requestBody);

    if (!mutateError) {
      formik.resetForm();
      setCreateErrorMessage('');
      handleClose();
      setRows(prevRows => [...prevRows, payload]);
      toast.success('Utworzono konto');
    } else {
      const errorMessage = getCreateAccountErrorMessage(payload);

      setCreateErrorMessage(errorMessage);
      toast.error(errorMessage);
    }
  };

  const formik = useFormik({
    initialValues: {
      group: 'staff',
      employee: null,
      send_email_with_credentials: true,
      camps: [],
      zs_weeks_periods: []
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: values => {
      console.log('fv', values);
      handleSubmit(values);
    }
  });

  return (
    <Modal open={true} onClose={handleClose}>
      <Card sx={{ ...style, width: '35vw' }}>
        <CardHeader
          title="Utwórz konto użytkownika"
          action={
            <Tooltip title="zamknij">
              <IconButton color="primary" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          }
          sx={{ textAlign: 'center' }}
        />
        <CardContent>
          <form
            onSubmit={formik.handleSubmit}
            style={{ display: 'flex', flexDirection: 'column' }}
          >
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
            ></Stack>
            <Box height={20} />
            <FormControl>
              <FormLabel>Typ konta</FormLabel>
              <RadioGroup
                name="group"
                value={formik.values.group}
                onChange={formik.handleChange}
              >
                <FormControlLabel
                  value="staff"
                  control={<Radio />}
                  label="Pracownik standardowy"
                />
                <FormControlLabel
                  value="managers"
                  control={<Radio />}
                  label="Kadra zarządzająca"
                />
              </RadioGroup>
            </FormControl>
            <Box height={20} />
            <TextField
              label="Szukaj pracowników"
              value={employeeSearch}
              onChange={event => setEmployeeSearch(event.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              placeholder="Wpisz nazwisko, email, ID pracownika lub ID aplikacji"
            />
            <FormControl fullWidth ref={employeeSelectContainerRef}>
              <InputLabel>Powiązany pracownik</InputLabel>
              <Select
                name="employee"
                value={formik.values.employee}
                onChange={event => formik.setFieldValue('employee', event.target.value)}
                renderValue={selected => {
                  const selectedOption = _.find(employeeOptions, { value: selected });

                  return selectedOption
                    ? `${selectedOption.label} | Aplikacje: ${selectedOption.applicationsLabel}`
                    : '';
                }}
                fullWidth
                input={<OutlinedInput label="Powiązany pracownik" />}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 420,
                      width: `${employeeMenuWidth}px`,
                      maxWidth: `${employeeMenuWidth}px`
                    }
                  },
                  MenuListProps: {
                    sx: {
                      width: '100%'
                    }
                  }
                }}
              >
                {!_.isEmpty(filteredEmployeeOptions)
                  ? _.map(filteredEmployeeOptions, option => (
                    <MenuItem
                      value={option.value}
                      key={option.value}
                      disabled={option.disabled}
                      style={{ whiteSpace: 'normal' }}
                    >
                      <Stack spacing={0.25}>
                        <Typography
                          variant="body2"
                          color={
                            option.disabled ? 'text.disabled' : 'text.primary'
                          }
                        >
                          {option.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{}}
                          color={
                            option.disabled
                              ? 'text.disabled'
                              : 'text.secondary'
                          }
                        >
                          Email: {option.email || '-'}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={
                            option.disabled
                              ? 'text.disabled'
                              : 'text.secondary'
                          }
                        >
                          Aplikacje: {option.applicationsLabel}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))
                  : []}
              </Select>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Wyniki: {filteredEmployeeOptions.length} /{' '}
                {employeeOptions.length}
              </Typography>
            </FormControl>
            {formik.values.group === 'managers' ? (
              <>
                <Box height={20} />
                <FormControl>
                  <InputLabel>Turnusy spoza aplikacji</InputLabel>
                  <Select
                    multiple
                    name="camps"
                    id="camps_field"
                    value={formik.values.camps}
                    onChange={formik.handleChange}
                    fullWidth
                  >
                    {!_.isEmpty(choices.camps)
                      ? _.map(choices.camps, obj => (
                        <MenuItem
                          value={obj.value}
                          key={obj.value}
                          style={{ whiteSpace: 'normal' }}
                        >
                          {obj.label}
                        </MenuItem>
                      ))
                      : []}
                  </Select>
                </FormControl>
                <Box height={20} />
                <FormControl>
                  <InputLabel>Terminy ZS spoza aplikacji</InputLabel>
                  <Select
                    multiple
                    name="zs_weeks_periods"
                    id="zs_weeks_periods_field"
                    value={formik.values.zs_weeks_periods.map(obj => obj.name)}
                    onChange={event => {
                      const selectedValues = event.target.value;
                      const selectedObjects = choices.zs_weeks_periods.filter(
                        obj => selectedValues.includes(obj.name)
                      );
                      formik.setFieldValue('zs_weeks_periods', selectedObjects);
                    }}
                    fullWidth
                  >
                    {!_.isEmpty(choices.zs_weeks_periods)
                      ? _.map(choices.zs_weeks_periods, obj => (
                        <MenuItem
                          value={obj.name}
                          key={obj.name}
                          style={{ whiteSpace: 'normal' }}
                        >
                          {obj.name}
                        </MenuItem>
                      ))
                      : []}
                  </Select>
                </FormControl>
              </>
            ) : null}
            <Box height={20} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.send_email_with_credentials}
                  onChange={event => formik.setFieldValue(
                    'send_email_with_credentials',
                    event.target.checked
                  )
                  }
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              }
              label="Wyślij maila z danymi do pracownika"
            />
            {error ? (
              <>
                <Box height={40} />
                <Alert variant="outlined" severity="error">
                  {createErrorMessage || 'Nie udało się utworzyć konta'}
                </Alert>
              </>
            ) : null}
            <Box height={40} />
            {loading ? (
              <>
                <LinearProgress color="success" />
              </>
            ) : (
              <Button
                disabled={!formik.values.employee}
                type="submit"
                variant="contained"
              >
                Zatwierdź
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </Modal>
  );
}
